import type {Draft} from 'immer'
import {produce} from 'immer'

import React, {useMemo, useCallback, useRef} from 'react'
import {arrayMove, List} from 'react-movable'
import type {RenderItemParams} from 'react-movable/lib/types'

import {useAppDispatch, useAppSelector} from '../../../../../../../../../hooks/react-redux'
import {getDraftJobs} from '../../../../../../EditPipelinePage/EditPipelinePage.selectors'
import {
  pipelineDraft,
  suggestions,
} from '../../../../../../EditPipelinePage/slices/EditPipelinePage.slices'
import type {pipelineHeadFragment$key} from '../../../../../../hooks/__generated__/pipelineHeadFragment.graphql'
import {usePipelineId} from '../../../../../../hooks/pipeline'
import type {Step} from '../../../../../../types'
import {StepTypes} from '../../../../../../types'
import SettingsBlock from '../../../../SettingsBlock/SettingsBlock'

import useJobStepsStoredValue from './hooks/useJobStepsStoredValue'
import JobStep from './JobStep/JobStep'
import {WORKING_DIRECTORY} from './JobStep/JobStep.consts'
import {DOCKER_IMAGE_PROPERTY_NAME} from './JobStep/JobStepRunInDocker/JobStepRunInDocker.consts'

import styles from './JobSteps.css'

type Props = {
  jobId: string
  pipelineHeadKey: pipelineHeadFragment$key | null
}

function JobSteps({jobId, pipelineHeadKey}: Props) {
  const id = usePipelineId()
  const jobStepListStore = useAppSelector(state => getDraftJobs(state, id)?.[jobId]?.steps)
  const stepList = useMemo(() => jobStepListStore ?? [], [jobStepListStore])

  const {addStoredJobStep, getStoredJobStep} = useJobStepsStoredValue(stepList, jobId)
  const reordered = useAppSelector(
    state => state.pipelines.pipelineDraft[id]?.reordered?.steps?.[jobId],
  )
  const order = stepList?.map((_, i) => reordered?.[i] ?? i)
  const dispatch = useAppDispatch()
  const updateSteps = useCallback(
    (recipe: (draft: Draft<Step[]>) => void, index: number) => {
      const steps = produce(stepList ?? [], recipe)
      const storedStep = steps[index]

      addStoredJobStep(storedStep, index)

      dispatch(
        pipelineDraft.actions.setSteps({
          id,
          jobId,
          steps,
        }),
      )
    },
    [stepList, addStoredJobStep, dispatch, id, jobId],
  )
  const onStepPropertiesChange = useCallback(
    (index: number, properties: Array<{name: string; value: string | null}>) => {
      updateSteps((draft: Draft<Step[]>) => {
        const step = draft[index]

        for (const {name, value} of properties) {
          if (value == null) {
            delete step[name]
          } else {
            step[name] = value
          }
        }
      }, index)
    },
    [updateSteps],
  )
  const onStepPropertyChange = useCallback(
    (index: number, name: string, value: string | null) => {
      onStepPropertiesChange(index, [{name, value}])
    },
    [onStepPropertiesChange],
  )
  const onStepNameChange = useCallback(
    (index: number, name: string) => onStepPropertyChange(index, 'name', name),
    [onStepPropertyChange],
  )
  const onStepTypeChange = useCallback(
    (index: number, type: string) => {
      const stepId = `step-${jobId}-${index + 1}`
      dispatch(suggestions.actions.hideSuccessMessage({id: stepId}))
      updateSteps((draft: Draft<Step[]>) => {
        const prevWorkingDirecory = draft[index][WORKING_DIRECTORY]
        const prevStep = getStoredJobStep(type, index)
        const step: Step = prevStep ? prevStep : {type, [WORKING_DIRECTORY]: prevWorkingDirecory}

        draft[index] = step

        if (!prevStep && type === StepTypes.NODE) {
          step['shell-script'] = 'npm ci'
          step[DOCKER_IMAGE_PROPERTY_NAME] = 'node:lts'
        }
      }, index)
    },
    [dispatch, getStoredJobStep, jobId, updateSteps],
  )
  const autoFocusStep = useRef<boolean>(false)
  const addStep = useCallback(() => {
    autoFocusStep.current = true
    updateSteps((draft: Draft<Step[]>) => {
      draft.push({
        type: StepTypes.CLI,
      })
    }, stepList.length)
  }, [stepList.length, updateSteps])
  const hasSteps = Boolean(stepList?.length)

  const renderItem = ({value, index, props}: Partial<RenderItemParams<number>>) => {
    const step = stepList[value!]
    return (
      step && (
        <div {...props} className={styles.stepWrapper}>
          <JobStep
            key={step.id ?? `${jobId}-step-${value}`}
            step={step}
            originalIndex={value ?? 0}
            index={index ?? 0}
            onStepPropertiesChange={onStepPropertiesChange}
            onStepPropertyChange={onStepPropertyChange}
            onStepNameChange={onStepNameChange}
            onStepTypeChange={onStepTypeChange}
            autoFocus={autoFocusStep.current && step.id == null}
            draggable={stepList.length > 1}
            pipelineHeadKey={pipelineHeadKey}
          />
        </div>
      )
    )
  }

  React.useEffect(() => {
    if (!hasSteps) {
      addStep()
    }
  }, [addStep, hasSteps])

  return (
    <>
      {hasSteps ? (
        <>
          {order.length > 1 ? (
            <List
              onChange={({oldIndex, newIndex}) =>
                dispatch(
                  pipelineDraft.actions.reorderSteps({
                    id,
                    jobId,
                    order: arrayMove(order, oldIndex, newIndex),
                  }),
                )
              }
              values={order}
              renderList={({children, props}) => <div {...props}>{children}</div>}
              renderItem={renderItem}
            />
          ) : (
            renderItem({value: 0, index: 0})
          )}
          <SettingsBlock
            id={'add-step'}
            title={`Add Step ${stepList.length + 1}`}
            onAddClick={addStep}
          />
        </>
      ) : (
        <SettingsBlock id={'no-steps'} title={'No Steps'} onAddClick={addStep} />
      )}
    </>
  )
}

export default JobSteps
